/**
 * Worker API client. Reads VITE_API_URL at build time and the admin session
 * token from localStorage at request time. Decodes the standard
 * `{ data, meta }` / `{ error }` envelope (see src/lib/envelope.ts).
 *
 * Storage:
 *   fooddb.admin_session — opaque session token issued by POST /v1/admin/login.
 *                          Sent on every authed request as `x-admin-session`.
 *   fooddb.device_id     — persistent UUID identifying this browser. Sent at
 *                          login so the session row records which device
 *                          owns it (surfaced on the Settings page).
 *
 * Legacy `fooddb.admin_token` (the raw secret) is wiped on first run if
 * encountered — we never want it persisted any more.
 */

import { emitUnauthorized } from "./auth-bus";

const SESSION_KEY = "fooddb.admin_session";
const DEVICE_KEY = "fooddb.device_id";
const LEGACY_TOKEN_KEY = "fooddb.admin_token";

export const apiUrl: string = (() => {
  const raw = import.meta.env.VITE_API_URL?.trim();
  if (!raw) {
    // In dev we fall back to localhost:8787 (wrangler dev's default port).
    return "http://localhost:8787";
  }
  return raw.replace(/\/$/, "");
})();

// One-time legacy cleanup: wipe the old raw-token key so the secret doesn't
// linger in storage after upgrade.
try {
  localStorage.removeItem(LEGACY_TOKEN_KEY);
} catch {
  // localStorage may be disabled (private mode + Safari); ignore.
}

export function getToken(): string | null {
  return localStorage.getItem(SESSION_KEY);
}

export function setToken(token: string): void {
  localStorage.setItem(SESSION_KEY, token);
}

export function clearToken(): void {
  localStorage.removeItem(SESSION_KEY);
}

/**
 * Persistent device id. Generated once per browser, lives in localStorage.
 * Sent in the login body so the server-side audit log can show which device
 * a session was issued to.
 */
export function getDeviceId(): string {
  let id = localStorage.getItem(DEVICE_KEY);
  if (!id) {
    id = (crypto as Crypto & { randomUUID: () => string }).randomUUID();
    localStorage.setItem(DEVICE_KEY, id);
  }
  return id;
}

type Envelope<T> =
  | { data: T; meta?: { request_id?: string; [k: string]: unknown } }
  | {
      error: { code: string; message: string; request_id?: string };
    };

export class ApiError extends Error {
  constructor(
    public code: string,
    message: string,
    public status: number,
  ) {
    super(message);
  }
}

export async function api<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers ?? {});
  headers.set("accept", "application/json");
  const token = getToken();
  if (token) headers.set("x-admin-session", token);
  if (init.body && !headers.has("content-type")) {
    headers.set("content-type", "application/json");
  }

  const res = await fetch(`${apiUrl}${path}`, { ...init, headers });
  const json = (await res.json().catch(() => ({}))) as Envelope<T>;

  if (!res.ok || "error" in json) {
    const error = "error" in json ? json.error : null;
    // Any 401 from any endpoint means the persisted token is stale (server
    // restarted with a different ADMIN_TOKEN, secret rotated, env mismatch,
    // etc.). Wipe it and bounce to login. Skip wiping when no token was sent
    // — that's expected on the very first load before the user has typed one.
    if (res.status === 401 && getToken() != null) {
      clearToken();
      emitUnauthorized();
    }
    throw new ApiError(
      error?.code ?? "HTTP_ERROR",
      error?.message ?? `HTTP ${res.status}`,
      res.status,
    );
  }
  return json.data;
}
